import {useEffect, useMemo} from "react";
import {useSetState} from "ahooks";
import ReactECharts from 'echarts-for-react'
import {DatePicker, Spin, Radio, message, Tooltip} from "antd";
import {SSegmentedTabs} from "shj-components";
import {isEmpty} from "lodash-es";
import dayjs from "dayjs";

import svg1 from '@/assets/measureChart1.svg'
import svg2 from '@/assets/measureChart2.svg'
import svg3 from '@/assets/measureChart3.svg'
import styles from './index.module.less'
import {measureRoomStatistics} from "@/services/crmTool.ts";
import {getNumberUnit} from "@/pages/roomMeasurementTools/roomMeasurementManage/utils.ts";
import {InfoCircleOutlined} from "@ant-design/icons";

export const LineChart = () => {
  const [state, setState] = useSetState<any>({
    loading: false,
    total: 0,
    arrivalRate: 0,
    signRate: 0,
    tendencyChartData: {},
    currentType: 'arrive',
    typeData: [{value: 'arrive', label: '到店率'}, {value: 'sign', label: '签单率'}],
    searchValue: {queryMonth: dayjs().format('YYYY-MM')},
    searchTimeData: [
      {value: 'WEEK', label: '本周'},
      {value: 'MONTH', label: '本月'},
      {value: 'YEAR', label: '本年'},
    ]
  })

  useEffect(() => {
    getData()
  }, [state.searchValue]);

  const getData = () => {
    const data: any = {...state.searchValue};
    if (isEmpty(state.searchValue)) {
      data.queryMonth = dayjs().format('YYYY-MM')
    }
    setState({loading: true})
    measureRoomStatistics(data).then(res => {
      setState({...res})
    }).finally(() => {
      setState({loading: false})
    })
  }

  const resultData = useMemo(() => {
    const dates = Object.keys(state.tendencyChartData) || []
    const rateData = [], noRateData = []
    dates.forEach(key => {
      rateData.push(Number(state.tendencyChartData[key][state.currentType === 'arrive' ? 'arrivalRate' : 'signRate']))
      noRateData.push(state.tendencyChartData[key][state.currentType === 'arrive' ? 'noArrivalRate' : 'noSignRate'])
    })
    return {dates, rateData, noRateData}
  }, [state.tendencyChartData, state.currentType])

  const titleStr = state.currentType === 'arrive' ? '一客率' : '一签率'
  const searchYear = state.searchValue.queryMonth ? state.searchValue.queryMonth?.slice?.(0, 4) : state.searchValue.queryTime !== 'YEAR' ? dayjs().format('YYYY') : ''
  const option = {
    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        return `
      <div style="border-radius: 5px; background: #FFF">
        <div style="color: #2D2D2E; margin-bottom: 5px">${(searchYear ? searchYear + '-' : '') + params[0].name}</div>
        <div style="padding: 5px 10px; 
          width: 210px;
          background: #fff;
          box-shadow: 6px 0 20px 0 #2257BC1A; 
          margin-bottom: 5px; 
          border-radius: 4px;
          display: flex;
          justify-content: space-between;
          align-items: center">
          <div style="display: flex; align-items: center">
            <div style="background: #0064FF; width: 8px; height: 8px; margin-right: 5px; border-radius: 10px"></div>
            <span style="color: #4E5969">量房客户${titleStr}</span> 
          </div>
          <span style="color: #1D2129; font-weight: bolder">${params[0].value}%</span>
        </div>
         <div style="padding: 5px 10px; 
          width: 210px;
          background: #fff;
          box-shadow: 6px 0 20px 0 #2257BC1A; 
          margin-bottom: 5px; 
          border-radius: 4px;
          display: flex;
          justify-content: space-between;
          align-items: center">
          <div style="display: flex; align-items: center;;">
            <div style="background: #A6CEE3; width: 8px; height: 8px; margin-right: 5px; border-radius: 10px;"></div>
            <span style="color: #4E5969">未量房客户${titleStr}</span> 
          </div>
          <span style="color: #1D2129; font-weight: bolder">${params[1].value}%</span>
        </div>
      </div>
    `;
      },
      padding: 10,
    },
    legend: {
      bottom: 0,
      icon: 'circle', // 修改图标形状为矩形（原为默认圆形）
      itemHeight: 8,
      itemGap: 20,
      selectorButtonGap: 5,
      itemStyle: {
        padding: 0
      },
      textStyle: {
        fontSize: 14,
      }
    },
    grid: {
      left: 15,
      right: 15,
      bottom: 30,
      containLabel: true,
      lineStyle: {
        color: '#eee',       // 网格线颜色
        width: 1,            // 线宽
        type: 'dashed',      // 虚线类型
        opacity: 0.8         // 透明度
      }
    },
    xAxis: [
      {
        type: 'category',
        data: resultData.dates,
        axisPointer: {
          lineStyle: {
            color: '#4080FF',
            width: 2,
          }
        },
        splitLine: {
          show: true
        }
      },
    ],
    yAxis: [
      {
        type: 'value',
        min: 0,
        max: 100,
        axisLabel: {
          formatter: '{value}',
          color: '#666'
        },
      }
    ],
    series: [
      {
        name: `量房客户${titleStr}`,
        type: 'line',
        smooth: true,
        symbolSize: 8,
        symbol: 'circle',
        itemStyle: {
          color: '#0064FF',
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1, // 渐变方向（从上到下）
            colorStops: [
              {offset: 0, color: 'rgba(0, 100, 255, 0.3)'}, // 顶部颜色
              {offset: 1, color: 'rgba(0, 100, 255, 0)'}  // 底部颜色
            ]
          }
        },
        emphasis: {
          focus: 'series',
          blurScope: 'coordinateSystem',
          itemStyle: {
            color: '#fff', // 悬停时颜色
            borderColor: '#4080FF', // 边框颜色
            borderWidth: 2, // 边框宽度
          },
        },
        data: resultData.rateData
      },
      {
        name: `未量房客户${titleStr}`,
        type: 'line',
        smooth: true,
        symbolSize: 8,
        symbol: 'circle',
        itemStyle: {
          color: '#A6CEE3',
        },
        emphasis: {
          focus: 'series',
          blurScope: 'coordinateSystem',
          itemStyle: {
            color: '#fff', // 悬停时颜色
            borderColor: '#A6CEE3', // 边框颜色
            borderWidth: 2, // 边框宽度
          },
        },
        data: resultData.noRateData
      }
    ]
  };

  const onSelectTime = (key: string) => {
    setState({searchValue: state.searchValue.queryTime === key ? {} : {queryTime: key}})
  }

  return (
      <Spin spinning={state.loading}>
        <div className={styles.wrapBox}>
          <div className={styles.searchBox}>
            <Radio.Group optionType="button" options={state.searchTimeData}
                         onChange={(e) => onSelectTime(e.target.value)}
                         value={state.searchValue.queryTime}/>
            {/* {state.searchTimeData?.map(item => <div key={item.value}
                                                    onClick={() => onSelectTime(item.value)}
                                                    className={state.searchValue.queryTime === item.value ? styles.timeActive : ''}>{item.label}</div>)}*/}
            <div>月份：<DatePicker picker={'month'}

                                  value={state.searchValue.queryMonth ? dayjs(state.searchValue.queryMonth) : null}
                                  onChange={(date) => {
                                    if (!date) {
                                      return message.error('时间不能为空')
                                    } else {
                                      setState({searchValue: {queryMonth: dayjs(date).format('YYYY-MM')}})
                                    }
                                  }}/>
            </div>
          </div>
          <div className={styles.rowBox}>
            <div className={styles.colBox}>
              <Tooltip overlayInnerStyle={{minWidth: 260,}}
                       title={'实际量房时间在查询时间内的所有线索'}>
                <InfoCircleOutlined className={styles.infoIcon}/>
              </Tooltip>
              <div>
                <p>量房数</p>
                <p className={styles.num}>{getNumberUnit(state.total, '', 2).numberValue}</p>
              </div>
              <img src={svg1} alt={'量房数'}/>
            </div>
            <div className={styles.colBox}>
              <Tooltip overlayInnerStyle={{minWidth: 260, maxWidth: 300, whiteSpace: 'pre-wrap'}}
                       title={`量房一客数/线索数（衡量量房服务对一客到店的影响）\n
（1）量房一客数：查询时间内变成一客并且实际量房时间早于一客到店的线索。\n
（2）线索数：派单时间在查询时间内的所有线索。`}>
                <InfoCircleOutlined className={styles.infoIcon}/>
              </Tooltip>
              <div>
                <p>量房客户一客率</p>
                <p className={styles.num}>{state.arrivalRate}%</p>
              </div>
              <img src={svg2} alt={''}/>
            </div>
            <div className={styles.colBox}>
              <Tooltip overlayInnerStyle={{minWidth: 260, maxWidth: 300, whiteSpace: 'pre-wrap'}}
                       title={`量房一签数/一客数（衡量量房服务对一签的影响）\n
（1）量房一签数：查询时间内变成一签并且实际量房时间早于一签的线索。\n
（2）一客数：在查询时间内变成一签的所有线索。`}>
                <InfoCircleOutlined className={styles.infoIcon}/>
              </Tooltip>
              <div>
                <p>量房客户一签率</p>
                <p className={styles.num}>{state.signRate}%</p>
              </div>
              <img src={svg3} alt={''}/>
            </div>
          </div>
        </div>
        <div className={styles.chartBox}>
          <div className={styles.chartSelectTitle}>
            <SSegmentedTabs options={state.typeData} value={state.currentType}
                            size={'large'}
                            onChange={(type) => {
                              setState({currentType: type})
                            }}/>
          </div>
          <ReactECharts option={option}/>
        </div>
      </Spin>
  );
}